import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsightsIcon from '@mui/icons-material/Insights';
import { Link as RouterLink } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';
import apiClient from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const fmt = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value).toLocaleString('en-IN');
};

const formatDay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
};

const formatMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', {
    month: 'short',
    year: 'numeric',
  });
};

function StatCard({ label, value, hint, tint }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, borderColor: `${tint}33`, height: '100%' }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color: tint, lineHeight: 1.2 }}>
        {fmt(value)}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

function AppColumn({ title, tint, rows }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tint }} />
        <Typography fontWeight={800}>{title}</Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
        }}
      >
        {rows.map((row) => (
          <StatCard key={row.label} tint={tint} {...row} />
        ))}
      </Box>
    </Paper>
  );
}

const AppTrends = () => {
  const { user } = useAuth();
  const canConnect = user?.role === 'app_admin' || user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [range, setRange] = useState('day');
  const [metric, setMetric] = useState('both');
  const [bucket, setBucket] = useState('');
  const [serviceAccountJson, setServiceAccountJson] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/app-trends', {
        params: { days: 30, months: 12 },
      });
      setData(res.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Could not load app trends');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connectPlay = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put('/app-trends/play-connection', {
        bucket,
        serviceAccountJson,
      });
      toast.success(res.data?.message || 'Play Store reports connected');
      setServiceAccountJson('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Could not connect Play Store');
    } finally {
      setSaving(false);
    }
  };

  const disconnectPlay = async () => {
    setSaving(true);
    try {
      await apiClient.delete('/app-trends/play-connection');
      toast.success('Play Store connection removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Could not remove connection');
    } finally {
      setSaving(false);
    }
  };

  const loksy = data?.totals?.loksy || {};
  const vyapar = data?.totals?.vyapar || {};
  const jobs = data?.totals?.jobAlerts || {};
  const coupons = data?.totals?.coupons || {};
  const play = data?.playStore || {};

  const chartRows = useMemo(() => {
    if (!data) return [];
    if (range === 'month') {
      return (data.monthly || []).map((row) => ({
        label: formatMonth(row.month),
        'Loksy installs': row.loksyInstalls,
        'Vyapar installs': row.vyaparInstalls,
        'Loksy users': row.loksyUsers,
        'Vyapar users': row.vyaparUsers,
        'Job alerts': row.jobAlerts,
        'Alert questions': row.jobAlertQueries,
        'Coupons issued': row.couponsIssued,
        'Coupons redeemed': row.couponsRedeemed,
      }));
    }
    return (data.daily || []).map((row) => ({
      label: formatDay(row.date),
      'Loksy installs': row.loksyInstalls,
      'Vyapar installs': row.vyaparInstalls,
      'Loksy users': row.loksyUsers,
      'Vyapar users': row.vyaparUsers,
      'Job alerts': row.jobAlerts,
      'Alert questions': row.jobAlertQueries,
      'Coupons issued': row.couponsIssued,
      'Coupons redeemed': row.couponsRedeemed,
    }));
  }, [data, range]);

  const tableRows = range === 'month' ? data?.monthly || [] : data?.daily || [];

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <InsightsIcon color="primary" />
          <Typography variant="h5" fontWeight={800}>
            App Trends
          </Typography>
          <Chip size="small" label="apptrends" variant="outlined" />
        </Stack>
        <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
          Day-wise and month-wise growth for Loksy and Vyapar, plus job alert and coupon activity.
          Times are India time.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button component={RouterLink} to="/admin-control-screens" size="small">
            ← Admin Control Screens
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {data?.note ? (
          <Alert severity={play.connected ? 'info' : 'warning'} sx={{ mb: 2 }}>
            {data.note}
            {play.latestPublishedDate
              ? ` Latest day Google has published: ${formatDay(play.latestPublishedDate)}.`
              : ''}
          </Alert>
        ) : null}

        {play.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {play.error}
          </Alert>
        ) : null}

        {canConnect ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography fontWeight={800} sx={{ mb: 0.5 }}>
              Play Store reports
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {play.connected
                ? `Connected as ${play.clientEmail || 'service account'} · bucket ${play.bucket}`
                : 'Paste the Play Console report bucket and a service account JSON. Give that account permission to view app information and download bulk reports (Loksy com.loksy_citizen, Vyapar com.loksy.mitra).'}
            </Typography>
            <Stack spacing={1.25}>
              <TextField
                size="small"
                label="Cloud Storage bucket"
                placeholder="pubsite_prod_rev_0123456789"
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
              />
              <TextField
                size="small"
                label="Service account JSON"
                placeholder='{"client_email":"...","private_key":"..."}'
                value={serviceAccountJson}
                onChange={(e) => setServiceAccountJson(e.target.value)}
                multiline
                minRows={3}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={connectPlay} disabled={saving || !bucket || !serviceAccountJson}>
                  Connect Play Store
                </Button>
                {play.connected ? (
                  <Button color="inherit" onClick={disconnectPlay} disabled={saving}>
                    Remove
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <AppColumn
            title="Loksy"
            tint="#7c3aed"
            rows={[
              { label: 'Play Store installs', value: loksy.installations, hint: `Today ${fmt(loksy.installsToday)} · month ${fmt(loksy.installsThisMonth)}` },
              { label: 'Registered users', value: loksy.registeredUsers, hint: `Today ${fmt(loksy.newToday)} · month ${fmt(loksy.newThisMonth)}` },
              { label: 'Still installed', value: loksy.currentDevices, hint: `Active devices ${fmt(loksy.activeDevices)}` },
              { label: 'Uninstalls this month', value: loksy.uninstallsThisMonth, hint: 'Play Store daily device uninstalls' },
              { label: 'Active today', value: loksy.activeToday, hint: `7 days ${fmt(loksy.active7d)} · 30 days ${fmt(loksy.active30d)}` },
              { label: 'Inactive accounts', value: loksy.inactiveAccounts, hint: `Active accounts ${fmt(loksy.activeAccounts)}` },
            ]}
          />
          <AppColumn
            title="Vyapar"
            tint="#0284c7"
            rows={[
              { label: 'Play Store installs', value: vyapar.installations, hint: `Today ${fmt(vyapar.installsToday)} · month ${fmt(vyapar.installsThisMonth)}` },
              { label: 'Registered users', value: vyapar.registeredUsers, hint: `Today ${fmt(vyapar.newToday)} · month ${fmt(vyapar.newThisMonth)}` },
              { label: 'Still installed', value: vyapar.currentDevices, hint: `Active devices ${fmt(vyapar.activeDevices)}` },
              { label: 'Uninstalls this month', value: vyapar.uninstallsThisMonth, hint: 'Play Store daily device uninstalls' },
              { label: 'Approved shops', value: vyapar.approved, hint: `Pending ${fmt(vyapar.pending)} · rejected ${fmt(vyapar.rejected)}` },
              { label: 'Active today', value: vyapar.activeToday, hint: `7 days ${fmt(vyapar.active7d)} · drafts ${fmt(vyapar.drafts)}` },
            ]}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <AppColumn
            title="Job alerts"
            tint="#b45309"
            rows={[
              { label: 'Alerts published', value: jobs.total, hint: `Today ${fmt(jobs.newToday)} · month ${fmt(jobs.newThisMonth)}` },
              { label: 'Alert views', value: jobs.views, hint: 'All-time opens counted on each alert' },
              { label: 'Questions asked', value: jobs.queries, hint: `Today ${fmt(jobs.queriesToday)} · month ${fmt(jobs.queriesThisMonth)}` },
              { label: 'Subscribers', value: jobs.subscribers, hint: `Active ${fmt(jobs.activeSubscribers)} · today ${fmt(jobs.subscribersToday)}` },
            ]}
          />
          <AppColumn
            title="Coupons"
            tint="#be123c"
            rows={[
              { label: 'Coupons issued', value: coupons.issued, hint: `Today ${fmt(coupons.issuedToday)} · month ${fmt(coupons.issuedThisMonth)}` },
              { label: 'Coupons redeemed', value: coupons.redeemed, hint: `Today ${fmt(coupons.redeemedToday)} · month ${fmt(coupons.redeemedThisMonth)}` },
            ]}
          />
        </Box>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Tabs
              value={range}
              onChange={(_e, value) => setRange(value)}
              sx={{ minHeight: 36 }}
            >
              <Tab value="day" label="Day wise (30 days)" sx={{ minHeight: 36 }} />
              <Tab value="month" label="Month wise (12 months)" sx={{ minHeight: 36 }} />
            </Tabs>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={metric}
              onChange={(_e, value) => value && setMetric(value)}
            >
              <ToggleButton value="both">Apps</ToggleButton>
              <ToggleButton value="installs">Installs</ToggleButton>
              <ToggleButton value="users">Users</ToggleButton>
              <ToggleButton value="hits">Job alerts & coupons</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={range === 'day' ? 2 : 0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {metric === 'both' || metric === 'installs' ? <Bar dataKey="Loksy installs" fill="#7c3aed" /> : null}
                {metric === 'both' || metric === 'installs' ? <Bar dataKey="Vyapar installs" fill="#0284c7" /> : null}
                {metric === 'both' || metric === 'users' ? <Bar dataKey="Loksy users" fill="#c4b5fd" /> : null}
                {metric === 'both' || metric === 'users' ? <Bar dataKey="Vyapar users" fill="#7dd3fc" /> : null}
                {metric === 'hits' ? <Bar dataKey="Job alerts" fill="#b45309" /> : null}
                {metric === 'hits' ? <Bar dataKey="Alert questions" fill="#f59e0b" /> : null}
                {metric === 'hits' ? <Bar dataKey="Coupons issued" fill="#be123c" /> : null}
                {metric === 'hits' ? <Bar dataKey="Coupons redeemed" fill="#fb7185" /> : null}
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ mt: 2, minWidth: 860 }}>
            <TableHead>
              <TableRow>
                <TableCell>{range === 'month' ? 'Month' : 'Day'}</TableCell>
                <TableCell align="right">Loksy Play installs</TableCell>
                <TableCell align="right">Vyapar Play installs</TableCell>
                <TableCell align="right">Loksy users</TableCell>
                <TableCell align="right">Vyapar users</TableCell>
                <TableCell align="right">Job alerts</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell align="right">Coupons issued</TableCell>
                <TableCell align="right">Coupons redeemed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...tableRows].reverse().map((row) => (
                <TableRow key={row.date || row.month}>
                  <TableCell>
                    {range === 'month' ? formatMonth(row.month) : formatDay(row.date)}
                  </TableCell>
                  <TableCell align="right">{fmt(row.loksyInstalls)}</TableCell>
                  <TableCell align="right">{fmt(row.vyaparInstalls)}</TableCell>
                  <TableCell align="right">{fmt(row.loksyUsers)}</TableCell>
                  <TableCell align="right">{fmt(row.vyaparUsers)}</TableCell>
                  <TableCell align="right">{fmt(row.jobAlerts)}</TableCell>
                  <TableCell align="right">{fmt(row.jobAlertQueries)}</TableCell>
                  <TableCell align="right">{fmt(row.couponsIssued)}</TableCell>
                  <TableCell align="right">{fmt(row.couponsRedeemed)}</TableCell>
                </TableRow>
              ))}
              {!loading && tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>No trend rows yet.</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          </Box>
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default AppTrends;
